// ============================================================================
// EncounterEditor — single-page Clinical Notes Engine.
//
// Replaces the tabbed EncounterWorkspace. The encounter IS the
// editable document: one long scroll, sections in clinical order, each
// section's data lives in a structured field on ClinicalNote (no
// free-text-then-extract roundtrip — that caused the AMPLE-history
// duplication bug).
//
// All order forms (Rx / Lab / Imaging / Referral / Ambulance) inline
// the existing per-form components, which already manage their own
// "+ Add" → form → list flow. Generate Documents dropdown lives in the
// header so the doctor can issue any of the 7 PDF types from anywhere
// on the page.
//
// Autosave behaviour — every field debounces 1.2s and saves
// independently. Save draft / Submit & lock are sticky at the bottom
// so they never scroll out of reach.
// ============================================================================

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import {
  getEncounter, updateEncounter, submitEncounter, addAddendum,
  setDiagnosesOfType, patchDiagnosis,
} from '../../api/encounters.js'
import { formatMrn } from '../../api/patients.js'
import { useAuth } from '../../auth/AuthProvider.jsx'
import { Card } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import SnippetPicker from '../../components/SnippetPicker.jsx'
import { isEncounterLocked } from '../../constants/index.js'
import { statusColor, formatDateTime, dobAge } from '../../utils/format.js'

import ChiefComplaintInput from './ChiefComplaintInput.jsx'
import VitalsForm from './VitalsForm.jsx'
import BedsideTestsForm from './BedsideTestsForm.jsx'
import ICD10Autocomplete from './ICD10Autocomplete.jsx'
// PR #73: concept-first picker replaces the raw ICD-10 autocomplete on
// J / K / M. Same onPick shape so downstream sync path is unchanged.
// ICD10Autocomplete import kept for now; the picker itself has a
// "search full ICD-10 catalogue" fallback that calls the same endpoint,
// so eventually ICD10Autocomplete could be removed in a cleanup PR.
import ClinicalConceptPicker from './ClinicalConceptPicker.jsx'
import PrescriptionForm from './PrescriptionForm.jsx'
import LabOrderForm from './LabOrderForm.jsx'
import ImagingOrderForm from './ImagingOrderForm.jsx'
import ReferralForm from './ReferralForm.jsx'
import AmbulanceForm from './AmbulanceForm.jsx'

import { AutosaveTextarea, AutosaveInput, Section } from './AutosaveField.jsx'
import GenerateDocumentsMenu from './documents/GenerateDocumentsMenu.jsx'
import CneSidebar from './CneSidebar.jsx'
import PatientIdentityBanner from '../../components/PatientIdentityBanner.jsx'

function saveStateOf(m) {
  if (m.isError) return 'error'
  if (m.isPending) return 'saving'
  if (m.isSuccess) return 'saved'
  return 'idle'
}

// Patient allergy alert — three states so "confirmed none" (NKDA) reads
// differently from "not yet asked". Driven by the patient-level allergy
// record (enc.patient_allergy_status / patient_allergies), so it reflects the
// CURRENT record even on an older encounter.
function AllergyBanner({ status, text }) {
  const s = status || 'unknown'
  if (s === 'present') {
    return (
      <div
        role="alert"
        className="mb-4 rounded-lg border-2 border-rose-300 bg-rose-50 px-4 py-2.5 flex items-start gap-2"
      >
        <span className="text-rose-600 text-lg leading-none mt-0.5">⚠</span>
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-wide text-rose-700">
            Allergies
          </div>
          <div className="text-sm font-semibold text-rose-900 break-words">
            {text || 'Recorded — see AMPLE history'}
          </div>
        </div>
      </div>
    )
  }
  if (s === 'none') {
    return (
      <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 flex items-center gap-2">
        <span className="text-emerald-600">✓</span>
        <span className="text-sm font-medium text-emerald-800">
          No known drug allergies (NKDA)
        </span>
      </div>
    )
  }
  return (
    <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 flex items-center gap-2">
      <span className="text-amber-600">•</span>
      <span className="text-sm font-medium text-amber-800">
        Allergies not yet recorded — please confirm with the patient.
      </span>
    </div>
  )
}

export default function EncounterEditor() {
  const { encounterId } = useParams()
  const nav = useNavigate()
  const qc = useQueryClient()
  const { user } = useAuth()

  const [submitError, setSubmitError] = useState(null)
  const [saveStamp, setSaveStamp] = useState(null)  // 'ok' | 'error' | null
  const [addendumText, setAddendumText] = useState('')

  // Refs for force-flushing autosaving editors before submit.
  const ccRef = useRef(null)
  const vitalsRef = useRef(null)
  // One ref per structured ClinicalNote field; key = field name.
  const fieldRefs = useRef({})

  const encQ = useQuery({
    queryKey: ['encounter', encounterId],
    queryFn: () => getEncounter(encounterId),
    enabled: Boolean(encounterId),
  })
  const enc = encQ.data

  // Invalidate-not-set: see existing rationale in EncounterWorkspace —
  // many parallel writes; setQueryData would race them.
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['encounter', encounterId] })

  // Auto-transition: if the doctor opens an encounter that's still in
  // 'scheduled' state (provisioned by a Booking but never started),
  // flip it to 'draft' once on mount. Side-effects:
  //   • The doctor's "Upcoming" tab stops listing it (only `scheduled`
  //     rows appear there).
  //   • The "My Draft Encounters" tab picks it up.
  //   • The encounter editor's lock-check sees draft → fully writable.
  // Idempotent — runs once per mount; subsequent saves skip via the
  // status comparison. Wrapped in a ref-guarded effect so React's
  // Strict-Mode double-invocation in dev can't fire two PATCHes.
  const startedRef = useRef(false)
  useEffect(() => {
    if (!enc) return
    if (enc.status !== 'scheduled') return
    if (startedRef.current) return
    startedRef.current = true
    updateEncounter(encounterId, { status: 'draft' })
      .then(() => {
        invalidate()
        // Also invalidate the doctor dashboard's upcoming list so the
        // tab badge updates immediately after the doctor enters.
        qc.invalidateQueries({ queryKey: ['doctor-upcoming-bookings'] })
        qc.invalidateQueries({ queryKey: ['encounters'] })
      })
      .catch(() => {
        // Don't block editing on a transition failure; the doctor can
        // still write notes, and a manual save later will succeed.
        startedRef.current = false
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enc?.status, encounterId])

  // ---- mutations -----------------------------------------------------

  const saveCC = useMutation({
    mutationFn: (chief_complaint) => updateEncounter(encounterId, { chief_complaint }),
    onSuccess: invalidate,
  })

  const saveVitals = useMutation({
    mutationFn: (vitals) =>
      updateEncounter(encounterId, { structured: { ...(enc?.structured || {}), vitals } }),
    onSuccess: invalidate,
  })

  // Single mutation for any ClinicalNote field write. The field name
  // travels alongside the value as part of the mutation variables, so
  // each call writes to the right column. Per-field save state is
  // derived by remembering which field was written most recently.
  const [lastWrittenField, setLastWrittenField] = useState(null)
  const saveNoteField = useMutation({
    mutationFn: ({ field, value }) =>
      updateEncounter(encounterId, {
        structured: { clinical_note: { [field]: value } },
      }),
    onSuccess: invalidate,
  })
  const mutateField = (field, value) => {
    setLastWrittenField(field)
    saveNoteField.mutate({ field, value })
  }

  // Per-bucket diagnosis mutations. Each one syncs ONE diagnosis_type
  // (provisional / differential / final) independently — picking a code
  // in the Differential section won't disturb the Provisional or Final
  // sections, even if the same ICD-10 code happens to be present in
  // more than one bucket.
  const saveProvisional = useMutation({
    mutationFn: (codes) => setDiagnosesOfType(encounterId, 'provisional', codes),
    onSuccess: invalidate,
  })
  const saveDifferential = useMutation({
    mutationFn: (codes) => setDiagnosesOfType(encounterId, 'differential', codes),
    onSuccess: invalidate,
  })
  const saveFinal = useMutation({
    mutationFn: (codes) => setDiagnosesOfType(encounterId, 'final', codes),
    onSuccess: invalidate,
  })

  const submit = useMutation({
    mutationFn: () => submitEncounter(encounterId),
    onSuccess: invalidate,
    onError: (e) => setSubmitError(e),
  })

  const addend = useMutation({
    mutationFn: (text) => addAddendum(encounterId, text),
    onSuccess: () => { invalidate(); setAddendumText('') },
  })

  // ---- derived state -------------------------------------------------

  const note = enc?.clinical_note || {}
  // Diagnoses bucketed by diagnosis_type so each section owns its own
  // list. The default bucket for legacy un-typed diagnoses is
  // "provisional" — that matches the historical behaviour of the
  // pre-restructure editor.
  const dxBuckets = useMemo(() => {
    const all = enc?.icd10_codes || []
    return {
      provisional:  all.filter((c) => (c._diagnosisType || 'provisional') === 'provisional'),
      differential: all.filter((c) => c._diagnosisType === 'differential'),
      final:        all.filter((c) => c._diagnosisType === 'final'),
    }
  }, [enc])
  const submittedLocked = enc && isEncounterLocked(enc.status)
  const myDoctorId = user?.profile?.id
  const isAdmin = Boolean(user?.is_staff || user?.is_superuser)
  const isOwner = isAdmin || (myDoctorId != null && enc?.doctor === myDoctorId)
  const readOnly = Boolean(submittedLocked || !isOwner)

  // ---- save / submit -------------------------------------------------

  function flushAll() {
    ccRef.current?.flush?.()
    vitalsRef.current?.flush?.()
    Object.values(fieldRefs.current).forEach((r) => r?.flush?.())
  }

  async function handleSaveDraft() {
    setSaveStamp(null)
    try {
      flushAll()
      setSaveStamp('ok')
      setTimeout(() => setSaveStamp(null), 2500)
    } catch {
      setSaveStamp('error')
    }
  }

  async function handleSubmit() {
    flushAll()
    submit.mutate()
  }

  // ---- field saveState helper ----------------------------------------

  function fieldSaveState(field) {
    if (lastWrittenField !== field) return 'idle'
    return saveStateOf(saveNoteField)
  }

  // ---- render --------------------------------------------------------

  if (encQ.isLoading) return <div className="p-6 text-sm text-slate-400">Loading encounter…</div>
  if (encQ.isError || !enc) {
    return (
      <div className="space-y-3">
        <ErrorBanner error={encQ.error || new Error('Encounter not found')} onRetry={() => encQ.refetch()} />
        <button onClick={() => nav('/doctor')} className="text-xs underline">Back to dashboard</button>
      </div>
    )
  }

  // Helper to bind a structured field to its autosave component.
  const fieldProps = (key) => ({
    value: note[key] || '',
    onSave: (v) => mutateField(key, v),
    saveState: fieldSaveState(key),
    disabled: readOnly,
    ref: (el) => { fieldRefs.current[key] = el },
  })

  return (
    <div key={encounterId} className="pb-32">
      {/*
        Patient Identity Verification banner — sits above the encounter
        header. Reads its own patient data from /api/patients/<id>/ so
        every clinician sees a fresh confidence tier (photo + verified /
        moderate / unverified) at the top of every encounter. Write
        actions gated on !readOnly; backend re-enforces role permissions.
      */}
      {enc.patient && (
        <PatientIdentityBanner
          patientId={enc.patient}
          canWrite={!readOnly}
        />
      )}

      {/* ─── Header (full-width, sits above the two-column workspace) ─ */}
      <Card className="p-3 sm:p-4 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-slate-400">Clinical Encounter</div>
            <div className="font-semibold truncate text-lg">
              {enc.patient_name || 'Patient'}
            </div>
            <div className="text-xs text-slate-500 flex flex-wrap items-center gap-2 mt-0.5">
              {enc.patient && (
                <span>
                  <span className="text-[10px] uppercase tracking-wide text-slate-400 mr-1">MRN</span>
                  <span className="font-mono">{formatMrn(enc.patient)}</span>
                </span>
              )}
              {enc.patient_dob && <span>· {dobAge(enc.patient_dob)}</span>}
              {enc.patient_sex && <span className="capitalize">· {enc.patient_sex}</span>}
              <span className={`uppercase border rounded-full px-2 py-0.5 text-[10px] ${statusColor(enc.status)}`}>{enc.status}</span>
              {enc.updated_at && <span>Updated {formatDateTime(enc.updated_at)}</span>}
              {enc.submitted_at && <span>Submitted {formatDateTime(enc.submitted_at)}</span>}
            </div>
            {(enc.patient_phone || enc.patient_address || enc.patient_emergency_contact) && (
              <div className="text-xs text-slate-500 flex flex-wrap items-center gap-x-2 mt-0.5">
                {enc.patient_phone && <span>☎ {enc.patient_phone}</span>}
                {enc.patient_address && <span>· {enc.patient_address}</span>}
                {enc.patient_emergency_contact && (
                  <span>· Emergency: {enc.patient_emergency_contact}</span>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-2 items-center">
            <Link to="/doctor" className="px-3 py-2 border rounded text-sm bg-white hover:bg-slate-50">Back</Link>
            <GenerateDocumentsMenu encounter={enc} />
          </div>
        </div>

        {isOwner && !submittedLocked && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1 border rounded-full px-2 py-0.5 bg-amber-50 border-amber-200 text-amber-800">
              Draft · private to you
            </span>
            <span>Everything you type saves automatically. <b>Save draft</b> to commit now; <b>Submit &amp; lock</b> to finalize.</span>
            {saveStamp === 'ok'    && <span className="text-emerald-700 font-medium">Saved ✓</span>}
            {saveStamp === 'error' && <span className="text-rose-600 font-medium">Save failed</span>}
          </div>
        )}
        {readOnly && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
            <span className="inline-flex items-center gap-1 border rounded-full px-2 py-0.5 bg-slate-100 border-slate-200 text-slate-700">
              Read-only reference
            </span>
            <span className="text-slate-500">
              {isOwner
                ? <>You submitted this encounter {enc?.submitted_at ? `on ${formatDateTime(enc.submitted_at)}` : ''}. Add follow-up via an <b>Addendum</b> below.</>
                : <>Authored by <b>Dr {enc?.doctor_name || 'Colleague'}</b>{enc?.submitted_at ? ` · submitted ${formatDateTime(enc.submitted_at)}` : ''}. Read-only.</>
              }
            </span>
          </div>
        )}
        {submitError && <div className="mt-3"><ErrorBanner error={submitError} /></div>}
      </Card>

      {/* ─── Allergy alert (clinical safety) — always shown, 3-state ── */}
      <AllergyBanner
        status={enc.patient_allergy_status}
        text={enc.patient_allergies}
      />

      {/* ─── Two-column workspace ─────────────────────────────────────
          Sticky left rail (section navigator with completion dots) +
          scrolling main column (the clinical sections themselves).
          The left rail collapses to a horizontal scroll chip strip on
          screens narrower than `lg` so mobile keeps the long-scroll feel
          without losing access to section jumping. */}
      <div className="lg:flex lg:gap-6 lg:items-start">
        <CneSidebar note={note} encounter={enc} dxBuckets={dxBuckets} />

        {/* Main scrolling column — `min-w-0` is required so flex children
            with long inline content (e.g. truncated patient names, code
            blocks) don't push the whole layout horizontally. */}
        <div className="flex-1 min-w-0 space-y-4">

      {/* ─── A. Chief complaint ─────────────────────────────────────── */}
      <Section id="sec-chief-complaint" title="A. Chief complaint" hint="Patient's reason for visit, in their own words">
        <ChiefComplaintInput
          ref={ccRef}
          value={enc.chief_complaint || ''}
          disabled={readOnly}
          onSave={(v) => saveCC.mutate(v)}
          saveState={saveStateOf(saveCC)}
        />
      </Section>

      {/* ─── B. History of presenting illness ───────────────────────── */}
      <Section id="sec-hpi" title="B. History of presenting illness" hint="Onset, character, timing, aggravating / relieving factors, associated symptoms">
        {!readOnly && (
          <div className="flex justify-end mb-1">
            <SnippetPicker
              filter={{ section: 'B' }}
              onInsert={(text) => fieldRefs.current.history_of_presenting_illness?.insert(text)}
            />
          </div>
        )}
        <AutosaveTextarea
          rows={6}
          placeholder="Onset, character, severity, timing, aggravating/relieving factors, associated symptoms…"
          {...fieldProps('history_of_presenting_illness')}
        />
      </Section>

      {/* ─── C. AMPLE history ───────────────────────────────────────── */}
      <Section id="sec-ample" title="C. AMPLE history" hint="Allergies · Medications · Past medical history · Last oral intake · Events leading up">
        <div className="space-y-3">
          <AutosaveInput    label="Allergies"               placeholder="e.g. Nil known" {...fieldProps('ample_allergies')} />
          <AutosaveInput    label="Current medications"     placeholder="e.g. Nil"        {...fieldProps('ample_medications')} />
          <AutosaveTextarea label="Past medical history" rows={2} placeholder="Conditions, surgeries, hospital admissions" {...fieldProps('ample_past_medical_history')} />
          <AutosaveInput    label="Last oral intake"         placeholder="e.g. 2 hours ago, light meal" {...fieldProps('ample_last_meal')} />
          <AutosaveTextarea label="Events leading to presentation" rows={2} placeholder="Sequence of events leading up to the encounter" {...fieldProps('ample_events')} />
        </div>
      </Section>

      {/* ─── D. Review of systems ───────────────────────────────────── */}
      <Section id="sec-ros" title="D. Review of systems" hint="Symptoms by system. 'Nil' is a valid clinical entry.">
        {!readOnly && (
          <div className="flex justify-end mb-1">
            <SnippetPicker
              filter={{ section: 'D' }}
              onInsert={(text) => fieldRefs.current.review_of_systems?.insert(text)}
            />
          </div>
        )}
        <AutosaveTextarea rows={4} placeholder="CVS, RS, GI, GU, MSK, CNS, …" {...fieldProps('review_of_systems')} />
      </Section>

      {/* ─── E. Vitals ──────────────────────────────────────────────── */}
      <Section id="sec-vitals" title="E. Vital signs (Objective)" hint="Temperature, pulse, BP, respiration, SpO₂, weight, height">
        <VitalsForm
          ref={vitalsRef}
          value={enc.structured?.vitals || {}}
          disabled={readOnly}
          onSave={(v) => saveVitals.mutate(v)}
          saveState={saveStateOf(saveVitals)}
        />
      </Section>

      {/* ─── F. Bedside tests ───────────────────────────────────────── */}
      {/* Catalogue-driven point-of-care tests (urine dip, ECG, capillary
          glucose, etc.). Each row is billable — `unit_price_pgk` is
          snapshotted server-side from the per-org rate card or the
          catalogue indicative price. Tests tagged `is_screening_eligible`
          feed corporate medical-screening tallies (Phase 2). */}
      <Section id="sec-bedside" title="F. Bedside tests" hint="Point-of-care · billable · corporate-screening eligible where flagged">
        <BedsideTestsForm encounter={enc} locked={readOnly} />
      </Section>

      {/* ─── G. Examination ─────────────────────────────────────────── */}
      <Section id="sec-exam" title="G. Physical examination" hint="General · system-by-system findings">
        {!readOnly && (
          <div className="flex justify-end mb-1">
            <SnippetPicker
              filter={{ section: 'G' }}
              onInsert={(text) => fieldRefs.current.physical_examination?.insert(text)}
            />
          </div>
        )}
        <AutosaveTextarea rows={6} placeholder="General appearance, then system-by-system…" {...fieldProps('physical_examination')} />
      </Section>

      {/*
        ─── H + I. Lab orders + Imaging orders (side-by-side) ─────────
        Investigations go RIGHT after the physical examination — that's
        the natural clinical flow ("examined the patient → ordered
        tests"). On screens >= 1024px (Tailwind `lg:`) the two cards
        sit side-by-side as two columns; on smaller screens they stack
        with Lab first. Each card is its own Section so the existing
        +Add inline form / list UI continues to work unchanged.
      */}
      {/* H+I share one sidebar anchor (sec-investigations) — the wrapping
          div carries the id so the rail jumps to the top of the two-card
          row regardless of which side the doctor wants to land on. */}
      <div id="sec-investigations" className="grid grid-cols-1 xl:grid-cols-2 gap-4 scroll-mt-24">
        <Section title="H. Lab orders" hint="Tests to be performed by the laboratory">
          <LabOrderForm encounter={enc} locked={readOnly} />
        </Section>
        <Section title="I. Imaging orders" hint="X-ray, ultrasound, CT, MRI requests">
          <ImagingOrderForm encounter={enc} locked={readOnly} />
        </Section>
      </div>

      {/* ─── J. Provisional diagnosis ──────────────────────────────── */}
      <Section
        id="sec-provisional"
        title="J. Provisional diagnosis"
        hint="Main working diagnosis · type clinical concept (e.g. diab, malar, CAP)"
      >
        {!readOnly && dxBuckets.provisional.length === 0 && (
          <div className="mb-2">
            <ClinicalConceptPicker
              placeholder="Search clinical concept for the working diagnosis…"
              excludeCodes={dxBuckets.provisional.map((c) => c.code)}
              onPick={(c) => saveProvisional.mutate([c])}
            />
          </div>
        )}
        <DxChipList
          codes={dxBuckets.provisional}
          readOnly={readOnly}
          onRemove={(c) => saveProvisional.mutate(dxBuckets.provisional.filter((x) => x.code !== c.code))}
          single
          replacingHint="One provisional dx at a time. Remove the current one to change it."
        />
      </Section>

      {/* ─── K. Differential diagnoses ─────────────────────────────── */}
      <Section
        id="sec-differential"
        title="K. Differential diagnoses"
        hint={`${dxBuckets.differential.length} entit${dxBuckets.differential.length === 1 ? 'y' : 'ies'} · multiple allowed`}
      >
        {!readOnly && (
          <div className="mb-2">
            <ClinicalConceptPicker
              placeholder="Add a differential diagnosis…"
              excludeCodes={dxBuckets.differential.map((c) => c.code)}
              onPick={(c) => saveDifferential.mutate([...dxBuckets.differential, c])}
            />
          </div>
        )}
        <DxChipList
          codes={dxBuckets.differential}
          readOnly={readOnly}
          onRemove={(c) => saveDifferential.mutate(dxBuckets.differential.filter((x) => x.code !== c.code))}
          emptyHint="No differentials added yet."
        />
      </Section>

      {/*
        ─── K. Empirical treatment ────────────────────────────────────
        What the doctor started BEFORE final diagnosis is confirmed
        (e.g. empirical antibiotics for suspected pneumonia while
        awaiting culture; analgesia for suspected appendicitis pending
        imaging). Sits between the diagnostic reasoning (provisional /
        differential) and the definitive Final dx + Treatment plan,
        mirroring how clinicians actually think through cases.
      */}
      <Section
        id="sec-empirical"
        title="L. Empirical treatment"
        hint="Started before final dx confirmed · treat-and-investigate"
      >
        {!readOnly && (
          <div className="flex justify-end mb-1">
            <SnippetPicker
              filter={{ section: 'L' }}
              onInsert={(text) => fieldRefs.current.empirical_treatment?.insert(text)}
            />
          </div>
        )}
        <AutosaveTextarea
          rows={4}
          placeholder="e.g. Amoxicillin 500 mg TDS empirically for suspected community-acquired pneumonia, pending sputum culture and CXR."
          {...fieldProps('empirical_treatment')}
        />
      </Section>

      {/* ─── M. Final diagnoses ────────────────────────────────────── */}
      <Section
        id="sec-final"
        title="M. Final diagnoses"
        hint={`${dxBuckets.final.length} confirmed · added once investigations are back`}
      >
        {!readOnly && (
          <div className="mb-2">
            <ClinicalConceptPicker
              placeholder="Add a final / confirmed diagnosis…"
              excludeCodes={dxBuckets.final.map((c) => c.code)}
              onPick={(c) => saveFinal.mutate([...dxBuckets.final, c])}
            />
          </div>
        )}
        <DxChipList
          codes={dxBuckets.final}
          readOnly={readOnly}
          onRemove={(c) => saveFinal.mutate(dxBuckets.final.filter((x) => x.code !== c.code))}
          emptyHint="No final diagnoses recorded yet."
        />
      </Section>

      {/* ─── N. Treatment plan ──────────────────────────────────────── */}
      <Section id="sec-plan" title="N. Treatment plan" hint="Definitive plan once dx is confirmed. Prescriptions go in O below.">
        {!readOnly && (
          <div className="flex justify-end mb-1">
            <SnippetPicker
              filter={{ section: 'N' }}
              onInsert={(text) => fieldRefs.current.treatment_plan?.insert(text)}
            />
          </div>
        )}
        <AutosaveTextarea rows={5} placeholder="Education, lifestyle advice, dressings, immobilisation, definitive therapy…" {...fieldProps('treatment_plan')} />
      </Section>

      {/* ─── O. Prescriptions ───────────────────────────────────────── */}
      <Section id="sec-prescriptions" title="O. Prescriptions" hint="Click + Add to write a script">
        <PrescriptionForm encounter={enc} locked={readOnly} />
      </Section>

      {/* ─── P. Referrals ───────────────────────────────────────────── */}
      <Section id="sec-referrals" title="P. Referrals" hint="Onward to specialist or higher-level facility">
        <ReferralForm encounter={enc} locked={readOnly} />
      </Section>

      {/* ─── Q. Ambulance ───────────────────────────────────────────── */}
      <Section id="sec-ambulance" title="Q. Ambulance request" hint="Pre-hospital transport (rare)">
        <AmbulanceForm encounter={enc} locked={readOnly} />
      </Section>

      {/* ─── R. Follow-up plan ──────────────────────────────────────── */}
      <Section id="sec-followup" title="R. Follow-up plan" hint="Review interval · safety-net · escalation criteria">
        {!readOnly && (
          <div className="flex justify-end mb-1">
            <SnippetPicker
              filter={{ section: 'R' }}
              onInsert={(text) => fieldRefs.current.follow_up_plan?.insert(text)}
            />
          </div>
        )}
        <AutosaveTextarea rows={4} placeholder="When to review, what to look out for, when to escalate…" {...fieldProps('follow_up_plan')} />
      </Section>

      {/* ─── Addendums (post-submit only) ───────────────────────────── */}
      {submittedLocked && (
        <Card>
          <div className="font-medium text-slate-800 mb-2">Addendums</div>
          {(enc.addendums || []).length === 0 && (
            <div className="text-xs text-slate-400 mb-2">No addendums yet.</div>
          )}
          {(enc.addendums || []).map((a, idx) => (
            <div key={idx} className="border border-slate-200 rounded p-2 mb-2 text-sm">
              <div className="text-[11px] text-slate-400 mb-1">{a.author || 'Provider'} · {formatDateTime(a.created_at)}</div>
              <div className="whitespace-pre-wrap">{a.text}</div>
            </div>
          ))}
          {isOwner ? (
            <>
              <textarea
                rows={2}
                value={addendumText}
                onChange={(e) => setAddendumText(e.target.value)}
                placeholder="Add an addendum…"
                className="w-full border rounded px-2 py-1.5 text-sm"
              />
              <div className="flex justify-end mt-2">
                <button
                  onClick={() => addendumText.trim() && addend.mutate(addendumText.trim())}
                  disabled={!addendumText.trim() || addend.isPending}
                  className="text-sm bg-slate-800 text-white rounded px-3 py-2 disabled:opacity-60"
                >{addend.isPending ? 'Saving…' : 'Append addendum'}</button>
              </div>
              {addend.isError && <div className="mt-2"><ErrorBanner error={addend.error} /></div>}
            </>
          ) : (
            <div className="text-[11px] text-slate-400 mt-1">
              Only the authoring doctor can append an addendum. To contribute clinically, start a new encounter for this patient.
            </div>
          )}
        </Card>
      )}

        </div>
        {/* ─── /Main scrolling column ─── */}
      </div>
      {/* ─── /Two-column workspace ─── */}

      {/* ─── Sticky save bar (only when editable) ───────────────────── */}
      {isOwner && !submittedLocked && (
        <div className="fixed bottom-0 inset-x-0 bg-white border-t-2 border-slate-200 shadow-2xl z-30">
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div className="text-xs text-slate-500">
              {saveStamp === 'ok'    && <span className="text-emerald-700 font-medium">Saved ✓</span>}
              {saveStamp === 'error' && <span className="text-rose-600 font-medium">Save failed</span>}
              {!saveStamp && <>Auto-saves continuously. Use the buttons to commit now.</>}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSaveDraft}
                className="px-4 py-2 border-2 border-wantok-700 text-wantok-700 bg-white rounded-lg text-sm font-semibold hover:bg-wantok-50"
              >Save draft</button>
              <button
                onClick={handleSubmit}
                disabled={submit.isPending}
                className="px-5 py-2 bg-wantok-700 hover:bg-wantok-800 text-white rounded-lg text-sm font-bold shadow disabled:opacity-60"
              >{submit.isPending ? 'Submitting…' : 'Submit & lock'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Chip list for an ICD-10 diagnosis bucket (Provisional / Differential
 * / Final). Single-mode renders one chip and a hint about how to
 * change it; multi-mode renders any number of removable chips.
 */
// ---------------------------------------------------------------------------
// DxChipList — one stacked row per diagnosis. Header chip shows the ICD-10
// code badge + the catalogue title + a remove button; below it, a free-text
// narrative textarea lets the doctor add clinical nuance the structured
// code cannot capture ("suspected metastatic", "in remission", "screening
// positive — confirm repeat in 6 weeks", etc.). The narrative renders BEFORE
// the code in parens on generated certificates so the document reads like a
// clinical letter, not a structured dump.
//
// Narrative autosaves on blur (debounced via the AutosaveTextarea component)
// — a 1.5s pause in typing fires a PATCH to /api/clinical-notes/diagnoses/<id>/
// with the new narrative. Empty narrative is allowed and preserves the
// legacy certificate appearance.
// ---------------------------------------------------------------------------
function DxChipList({ codes, readOnly, onRemove, single, replacingHint, emptyHint }) {
  if (codes.length === 0) {
    return (
      <div className="text-xs text-slate-400">
        {emptyHint || (single ? 'No diagnosis selected.' : 'No diagnoses yet.')}
      </div>
    )
  }
  return (
    <div className="space-y-3">
      {codes.map((c) => (
        <DxRow key={c._diagnosisId || c.code} code={c} readOnly={readOnly} onRemove={onRemove} />
      ))}
      {single && replacingHint && !readOnly && (
        <div className="text-[11px] text-slate-400 mt-1">{replacingHint}</div>
      )}
    </div>
  )
}

// One diagnosis row — header chip + narrative textarea. Owns its own local
// state for the narrative so typing is responsive; saves on debounce/blur
// via patchDiagnosis. Read-only mode (locked encounter) hides the textarea
// when empty and renders it as static text otherwise.
function DxRow({ code, readOnly, onRemove }) {
  const c = code
  const [narrative, setNarrative] = useState(c._narrative || '')
  const [saveState, setSaveState] = useState('idle')

  // Re-sync local state when the underlying diagnosis row changes
  // (e.g. cache invalidation after a parallel PATCH from another tab).
  // We only adopt the server value when the user isn't actively editing,
  // detected by the saveState being 'idle'.
  useEffect(() => {
    if (saveState === 'idle') setNarrative(c._narrative || '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c._narrative])

  async function persist(next) {
    if (!c._diagnosisId) return  // no row yet — nothing to PATCH
    if ((next || '') === (c._narrative || '')) {
      setSaveState('idle')
      return
    }
    setSaveState('saving')
    try {
      await patchDiagnosis(c._diagnosisId, { clinical_narrative: next })
      setSaveState('saved')
      setTimeout(() => setSaveState('idle'), 1500)
    } catch {
      setSaveState('error')
    }
  }

  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-baseline gap-2 min-w-0 flex-1">
          <span className="font-mono text-xs font-bold text-wantok-700 bg-wantok-50 border border-wantok-200 rounded px-1.5 py-0.5">
            {c.code}
          </span>
          <span className="text-sm font-medium text-slate-800 truncate">
            {c.description}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {saveState === 'saving' && <span className="text-[11px] text-slate-400">Saving…</span>}
          {saveState === 'saved' && <span className="text-[11px] text-emerald-700">Saved ✓</span>}
          {saveState === 'error' && <span className="text-[11px] text-rose-600">Save failed</span>}
          {!readOnly && (
            <button
              onClick={() => onRemove(c)}
              className="text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded w-6 h-6 inline-flex items-center justify-center text-base"
              aria-label={`Remove ${c.code}`}
              title="Remove"
            >×</button>
          )}
        </div>
      </div>
      {/* Narrative supplement. Hidden when locked AND empty (nothing to show);
          shown as static text when locked AND populated; editable otherwise. */}
      {readOnly ? (
        narrative ? (
          <div className="text-sm text-slate-700 whitespace-pre-wrap mt-1 pl-1 border-l-2 border-wantok-200">
            {narrative}
          </div>
        ) : null
      ) : (
        <AutosaveTextarea
          rows={2}
          value={narrative}
          onSave={(v) => { setNarrative(v); persist(v) }}
          placeholder="Optional clinical narrative — adds nuance the code can't capture (e.g. 'suspected metastatic', 'in remission', 'well-controlled on amlodipine'). Renders before the code in parens on certificates."
          saveState={saveState === 'saving' ? 'saving' : saveState === 'saved' ? 'ok' : saveState === 'error' ? 'error' : 'idle'}
        />
      )}
    </div>
  )
}
